import { CommonModule } from '@angular/common';
import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { combineLatest } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppCommonModule } from '../common.module';
import { NoResultsComponent } from '../components/no-results/no-results.component';
import { Checklist } from '../models/checklist.model';
import { ChecklistService } from '../services/checklist.service';
import { ContractorsService } from '../services/contractors.service';
import { SiteProjectTemplatesService } from '../services/site-project-templates.service';
import { UsersService } from '../services/users.service';
import { Roles } from '../utility/role';
import { DefaultTemplateDialogComponent } from './dialogs/default-template/default-template.dialog';
import { ProjectTemplateDialogComponent } from './dialogs/project-template/project-template.dialog';

@Component({
  selector: 'app-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AppCommonModule,
    NoResultsComponent,
  ],
})
export class TemplatesComponent {
  private siteProjectTemplatesService = inject(SiteProjectTemplatesService);

  ticketTemplates$ = combineLatest([this.contractorsService.getCurrentContractor(), this.usersService.getCurrentUser()]).pipe(
    filter(([contractor, user]) => user.role === Roles.superAdmin || contractor != null),
    switchMap(([contractor]) => this.checklistService.getList({ adhocContractor: contractor?.guid ?? null })),
    map((items) => items ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  unifiedTemplates$ = this.ticketTemplates$.pipe(
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  projectTemplates$ = this.siteProjectTemplatesService.getList().pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  roleS = computed(() => this.usersService.role());

  constructor(
    private checklistService: ChecklistService,
    private contractorsService: ContractorsService,
    private modalCtrl: ModalController,
    private usersService: UsersService,
  ) { }

  onTicketTemplate(item?: Checklist) {
    this.modalCtrl.create({ component: DefaultTemplateDialogComponent, componentProps: { item } }).then((m) => {
      m.present();

      m.onDidDismiss<Checklist>().then((data) => {
        if (data.data) {
          if (item) {
            this.checklistService.update(data.data);
          } else {
            const contractor = this.contractorsService.contractorS();
            data.data.contractor = contractor?.guid ?? null;
            this.checklistService.save(data.data);
          }
        }
      });
    });
  }

  onNewProjectTemplate() {
    this.modalCtrl.create({ component: ProjectTemplateDialogComponent }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.siteProjectTemplatesService.save(data.data);
        }
      });
    });
  }
}
